import { useCallback, useEffect, useRef, useState } from "react"

/**
 * @param {{
 *   enabled?: boolean,
 *   perPage?: number,
 *   deps?: any[],
 *   fetchPage: (page: number, perPage: number) => Promise<{ items: any[], total?: number }>,
 * }} config
 */
export default function useInfiniteApiList({
    enabled = true,
    perPage = 25,
    deps = [],
    fetchPage,
}) {
    const [items, setItems] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [isLoadingInitial, setIsLoadingInitial] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [error, setError] = useState(null)
    const inFlightRef = useRef(false)
    const fetchPageRef = useRef(fetchPage)

    useEffect(() => {
        fetchPageRef.current = fetchPage
    }, [fetchPage])

    const hasMore = items.length < total

    const loadPage = useCallback(async (nextPage, mode) => {
        if (!enabled || inFlightRef.current) {
            return
        }

        inFlightRef.current = true
        setError(null)
        if (mode === "append") {
            setIsLoadingMore(true)
        } else {
            setIsLoadingInitial(true)
        }

        try {
            const body = await fetchPageRef.current(nextPage, perPage)
            const nextItems = body?.items ?? []
            const nextTotal = Number(body?.total ?? 0)

            setTotal(Number.isFinite(nextTotal) ? nextTotal : 0)
            setPage(nextPage)
            setItems((current) => (
                mode === "append" ? [...current, ...nextItems] : nextItems
            ))
        } catch (e) {
            setError(e)
        } finally {
            if (mode === "append") {
                setIsLoadingMore(false)
            } else {
                setIsLoadingInitial(false)
            }
            inFlightRef.current = false
        }
    }, [enabled, perPage])

    useEffect(() => {
        if (!enabled) {
            setItems([])
            setTotal(0)
            setPage(1)
            setError(null)
            setIsLoadingInitial(false)
            setIsLoadingMore(false)
            return
        }
        loadPage(1, "replace")
    }, [enabled, loadPage, ...deps])

    const loadMore = useCallback(() => {
        if (!enabled || isLoadingInitial || isLoadingMore || !hasMore) {
            return
        }
        loadPage(page + 1, "append")
    }, [enabled, hasMore, isLoadingInitial, isLoadingMore, loadPage, page])

    return {
        items,
        total,
        page,
        hasMore,
        isLoadingInitial,
        isLoadingMore,
        isLoading: isLoadingInitial || isLoadingMore,
        error,
        loadMore,
        reload: () => loadPage(1, "replace"),
    }
}

